/* eslint-disable jsx-a11y/interactive-supports-focus */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable react/no-unknown-property */
import React from 'react';
import {
  useCarousel, Image, Grid, HStack, Badge,
  Icon,
  ICON_VIEW_GALLERY_OUTLINE,
  ICON_ARROW_UP,
  Skeleton,
} from '@doveit/bricks';
import * as styles from './Gallery.style';
import MultimediaContentThumbnail from '../../content/components/MultimediaContentThumbnail';
import MultimediaContentThumbnailSkeleton from '../../content/components/MultimediaContentThumbnail.skeleton';

export type GalleryItem = {
  id: string | number,
  picture: string,
  thumbnail: string,
  fileName?: string,
  selected?: boolean,
  hasWarning?: boolean,
  shown?: boolean,
};

export interface GalleryProps<T extends GalleryItem> {
  items: T[];
  loading?: boolean;
  initialActivePosition?: number;
  toolsSlot?: (params: { carousel: ReturnType<typeof useCarousel>; activeItem: T | undefined }) => React.ReactNode;
  actionsSlot?: (params: { carousel: ReturnType<typeof useCarousel> }) => React.ReactNode;
  itemActionsSlot?: (params: { carousel: ReturnType<typeof useCarousel>; activeItem: T | undefined }) => React.ReactNode;
  tagsSlot?: (params: { carousel: ReturnType<typeof useCarousel>; activeItem: T | undefined }) => React.ReactNode;
  onNext?: () => void;
  onPrev?: () => void;
  renderItem?: (item: T, isActive: boolean) => React.ReactNode;
  renderThumbnail?: (item: T, index: number, isActive: boolean, onClick: () => void) => React.ReactNode;
}

const Gallery = <T extends GalleryItem>({
  items,
  loading: isLoading = false,
  initialActivePosition = 0,
  actionsSlot,
  toolsSlot,
  itemActionsSlot,
  tagsSlot,
  onNext,
  onPrev,
  renderItem,
  renderThumbnail,
}: GalleryProps<T>) => {
  const carousel = useCarousel({ totalElements: items.length, initialActivePosition });
  const activePosition = carousel.activePosition ?? 0;

  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [showTopFade, setShowTopFade] = React.useState(false);
  const [showBottomFade, setShowBottomFade] = React.useState(true);

  const handleScroll = React.useCallback(() => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    setShowTopFade(scrollTop > 0);
    setShowBottomFade(Math.ceil(scrollTop + clientHeight) < scrollHeight - 1);
  }, []);

  React.useEffect(() => {
    const timer = setTimeout(handleScroll, 50);
    return () => clearTimeout(timer);
  }, [handleScroll, items]);

  React.useEffect(() => {
    if (carousel.jump) {
      carousel.jump(initialActivePosition);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialActivePosition]);

  const handleNext = React.useCallback(() => {
    if (carousel.next) carousel.next();
    if (onNext) onNext();
  }, [carousel, onNext]);

  const handlePrev = React.useCallback(() => {
    if (carousel.prev) carousel.prev();
    if (onPrev) onPrev();
  }, [carousel, onPrev]);

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        if (activePosition < items.length - 1) {
          handleNext();
        }
      } else if (e.key === 'ArrowLeft') {
        if (activePosition > 0) {
          handlePrev();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activePosition, items.length, handleNext, handlePrev]);

  const enhancedCarousel = {
    ...carousel,
    next: handleNext,
    prev: handlePrev,
  };

  return (
    <div css={styles.base}>
      <div
        className="gallery-thumbnails-container"
        css={styles.thumbnailsContainer}
      >
        <div css={styles.side}>
          {!isLoading && !!toolsSlot && (
            <div css={styles.filters}>
              {toolsSlot({ carousel: enhancedCarousel, activeItem: items[activePosition] })}
            </div>
          )}
          <div css={styles.filmStripWrapper}>
            <div css={styles.filmStripFadeTop} style={{ opacity: showTopFade ? 1 : 0 }} />
            <div
              className="gallery-thumbnails"
              css={styles.filmStrip}
              $hasFiltersSlot={!!toolsSlot}
              ref={scrollRef}
              onScroll={handleScroll}
            >
              <Grid gutter={25}>
                {isLoading ? (
                  Array.from({ length: 8 }).map((_, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Grid.Unit size={1 / 2} key={`skeleton-thumb-${index}`} addCSS={{ scrollSnapAlign: 'start' }}>
                      <MultimediaContentThumbnailSkeleton aspectRatio="3 / 2" />
                    </Grid.Unit>
                  ))
                ) : (
                  items.map((item, index) => (
                    ((item.shown !== false) && (
                      <Grid.Unit size={1 / 2} key={item.id} addCSS={{ scrollSnapAlign: 'start' }}>
                        {renderThumbnail ? renderThumbnail(item, index, activePosition === index, () => carousel.jump?.(index)) : (
                          <div title={item.fileName}>
                            <MultimediaContentThumbnail
                              picture={{ src: item.thumbnail, alt: `Foto ${index + 1}` }}
                              index={index + 1}
                              active={activePosition === index}
                              selected={item.selected}
                              hasWarning={item.hasWarning}
                              aspectRatio="3 / 2"
                              interactive
                              onClick={() => carousel.jump?.(index)}
                            />
                          </div>
                        )}
                      </Grid.Unit>
                    ))))
                )}
              </Grid>
            </div>
            <div css={styles.filmStripFadeBottom} style={{ opacity: showBottomFade ? 1 : 0 }} />
          </div>
        </div>
      </div>
      <div css={styles.main}>
        {actionsSlot && (
          <div css={styles.actions}>
            {actionsSlot({ carousel: enhancedCarousel })}
          </div>
        )}
        <div css={styles.mainPreview}>
          <div css={styles.topBar}>
            {!isLoading && (
              <div css={styles.overlayText}>
                <HStack gap={150} alignItems="center">
                  <Icon
                    path={ICON_VIEW_GALLERY_OUTLINE}
                    size={24}
                  />
                  <span>{activePosition + 1} / {items.length}</span>
                </HStack>
              </div>
            )}
            {!isLoading && itemActionsSlot && itemActionsSlot({ carousel: enhancedCarousel, activeItem: items[activePosition] })}
          </div>
          <div
            css={styles.carouselContainer}
            {...carousel.swipeHandlers}
          >
            {activePosition !== 0 && (
              <div
                role="button"
                css={styles.navigationPrevAction}
                onClick={handlePrev}
                aria-label="Vai alla foto precedente"
              >
                <div>
                  <Icon
                    path={ICON_ARROW_UP}
                    size={48}
                    direction="left"
                  />
                </div>
              </div>
            )}
            {(activePosition !== items.length - 1) && !isLoading && (
              <div
                role="button"
                css={styles.navigationNextAction}
                onClick={handleNext}
                aria-label="Vai alla foto successiva"
              >
                <div>
                  <Icon
                    path={ICON_ARROW_UP}
                    size={48}
                    direction="right"
                  />
                </div>
              </div>
            )}
            {isLoading ? (
              <div className="gallery-item">
                <div css={styles.mainImageWrapper}>
                  <Skeleton height="100%" />
                </div>
              </div>
            ) : (
              items.length > 0 && (
                <div key={items[activePosition].id} className="gallery-item">
                  {renderItem ? renderItem(items[activePosition], true) : (
                    <div css={styles.mainImageWrapper}>
                      <Image
                        src={items[activePosition].picture}
                        alt={`Item ${items[activePosition].id}`}
                        lazyLoad
                        responsive
                      />
                    </div>
                  )}
                </div>
              )
            )}
          </div>
          {!isLoading && (
            <div css={styles.bottomBar}>
              <div>
                {tagsSlot && tagsSlot({ carousel: enhancedCarousel, activeItem: items[activePosition] })}
              </div>
              {items[activePosition]?.fileName && (
                <div css={styles.fileName} title={items[activePosition].fileName}>
                  <Badge
                    aria-label="Badge nome file"
                    size="XS"
                    label={items[activePosition].fileName!}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Gallery;
